import { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import FormHelperText from '@mui/material/FormHelperText';
import CreateQuoteSectionTitle from '../CreateQuoteSectionTitle';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import { useGetClientOccupationByABNQuery } from '../../services/features/clientApi/clientApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const SearchOccupationField = ({ options }: { options: any }) => {
	const { currentQuotes, isReferred, isDeclined } = useQuoteContext();

    const [resetKey, setResetKey] = useState<number>(0);

	const quoteId = useSelector((state: RootState) => state.quoteSlice.quoteId);

	const formik = useMemo(() => options, [options]);

	const readonly = isReferred || isDeclined;

	// const [businessNumber, setBusinessNumber] = useState<string | null>(null);
	const defaultBusNumber = 'xxxx';

	const businessNumber = useMemo(() => formik?.values?.businessNumber, [formik]);
	const acnBusinessNumber = useMemo(() => formik?.values?.acnBusinessNumber, [formik]);

	const occupationNamesQuery = useGetClientOccupationByABNQuery(
		(businessNumber ?? acnBusinessNumber ?? '').length > 0
			? acnBusinessNumber ?? businessNumber ?? defaultBusNumber
			: defaultBusNumber
	);

	useEffect(() => {
		if (quoteId && occupationNamesQuery.data) {
			occupationNamesQuery.refetch();
		}
	}, [quoteId]);

	const occupationItem = useMemo(
		() => ({
			name: formik?.values?.occupation ?? '',
			id: formik?.values?.suboccupationId ?? '',
		}),
		[formik.values.occupation, formik.values.suboccupationId]
	);

	// useEffect(() => console.log('OCCUPATION ITEM', occupationItem), [occupationItem]);
	const hasInitialOccupation = useMemo(
		() => Boolean(formik?.values?.occupation && formik?.values?.suboccupationId),
		[formik.values.occupation, formik.values.suboccupationId]
	);

	const occupationOptions = useMemo(() => {
		return (occupationNamesQuery.data ?? []).length > 0
			? occupationNamesQuery.data
			: hasInitialOccupation
			? [occupationItem]
			: [];
	}, [occupationNamesQuery.data, occupationItem, hasInitialOccupation]);

	const occupationValue =
		formik.values.occupation && formik.values.suboccupationId ? occupationItem : undefined;

    useEffect(() => {
        setResetKey((prev) => prev + 1);
    }, [formik.values?.entityGroup?.id]);

	return (
		<>
			<CreateQuoteSectionTitle
				title={'Occupation / Industry'}
				tooltip={
					"This should be Insured's main occupation/industry. If the Insured has more than one occupation then you can disclose the additional activities later in the quote journey."
				}
			/>
			<Autocomplete
				id='select-occupation'
                key={`search-occupation-${resetKey}`}
				disableClearable
				loading={occupationNamesQuery.isLoading || occupationNamesQuery.isFetching}
				disabled={
					// readonly || occupationNames.length === 0 ||
					readonly ||
					(occupationNamesQuery.data ?? []).length === 0 ||
					currentQuotes.length > 0
				}
				options={occupationOptions}
				getOptionLabel={(e: any) => e?.name ?? ''}
				renderInput={(params) => {
					return (
						<TextField
							placeholder={
								(formik.values.occupation ?? '').length > 0
									? formik.values.occupation
									: 'Search occupation'
							}
							className='text'
							error={formik.touched.occupation && Boolean(formik.errors.occupation)}
							{...params}
							// value={formik.values.occupation}
						/>
					);
				}}
				renderOption={(props, option) => (
					<li {...props} key={`${option.id}-${option.name}`}>
						{option.name}
					</li>
				)}
				isOptionEqualToValue={(
					option: { id: number },
					value: { id: number } | undefined | null
				) => {
					if (!value || !value.id) return false;
					return option.id === value.id;
				}}
				onChange={(_, selectedValue) => {
					// console.log('Selected occupation', selectedValue);
					formik.setFieldValue('occupation', selectedValue?.name);
					formik.setFieldValue('suboccupationId', selectedValue?.id);
				}}
				value={occupationValue}
			/>
			<FormHelperText error={formik.touched.occupation && Boolean(formik.errors.occupation)}>
				{formik.touched.occupation && formik.errors.occupation}
			</FormHelperText>
		</>
	);
};

export default SearchOccupationField;
